import {
    AppConfigCoreSectionKey,
    AppConfigFeaturesZitoneo,
    AppConfigZitoneoSectionKey,
    AppSections,
    AppSectionsCore,
    AppSectionsProfile,
    AppSectionsZinantis,
    AppSectionsZitoneo,
    IAppConfig,
    OwnerConfigCoreSectionKey,
    OwnerConfigZitoneoSectionKey,
} from '../../types/appConfiguration/appConfigTypes';
import { IApplicationConfiguration } from '../../types/entities/IApplicationConfiguration';
import { IHotelOwnerConfigurationValue } from '../../types/entities/IHotelOwnerConfigurationValue';

export const APP_KEY = 'DEATHEATERS';

export const APP_CONFIG_INITIAL_STATE: IAppConfig = {
    sections: {
        [AppSections.CORE]: {
            isVisible: true,
            pages: {
                [AppSectionsCore.PROPERTIES]: true,
                [AppSectionsCore.BOOKINGS]: true,
                [AppSectionsCore.TAXES]: true,
                [AppSectionsCore.SUPPLEMENTS]: false,
                [AppSectionsCore.CONTRACTS]: false,
                [AppSectionsCore.STOP_SALES]: false,
                [AppSectionsCore.ARI]: true,
            },
        },
        [AppSections.ZINANTIS]: {
            isVisible: false,
            pages: {
                [AppSectionsZinantis.MEMBERS]: false,
                [AppSectionsZinantis.CONNECTION_REQUESTS]: false,
                [AppSectionsZinantis.CHANNEL_MANAGERS]: false,
            },
        },
        [AppSections.PROFILE]: {
            isVisible: true,
            pages: {
                [AppSectionsProfile.ACCOUNT]: true,
                [AppSectionsProfile.CONTACTS]: true,
                [AppSectionsProfile.PAYMENT]: true,
                [AppSectionsProfile.SETTINGS]: true,
            },
        },
        [AppSections.ZITONEO]: {
            isVisible: false,
            pages: {
                [AppSectionsZitoneo.HOTEL_CONTENT]: false,
            },
        },
    },
    features: {
        zitoneo: {
            serviceScraperPromptKey: '',
            competitorScrapingPromptKey: '',
        },
    },
};

// general
export const adaptAppConfiguration = (
    zinantisMembership: boolean,
    configData: IApplicationConfiguration[] = [],
    ownerConfigData: IHotelOwnerConfigurationValue[] = []
): IAppConfig => {
    let adaptedConfig: IAppConfig = { ...APP_CONFIG_INITIAL_STATE };
    // core section
    adaptedConfig = adaptSupplementSection(
        configData,
        ownerConfigData,
        adaptedConfig
    );

    adaptedConfig = adaptStopSalesSection(
        configData,
        ownerConfigData,
        adaptedConfig
    );

    adaptedConfig = adaptStaticContractsSection(
        configData,
        ownerConfigData,
        adaptedConfig
    );

    adaptedConfig = adaptZitoneoSection(
        configData,
        ownerConfigData,
        adaptedConfig
    );

    //zinantis section
    adaptedConfig = adaptZinantisSection(zinantisMembership, adaptedConfig);

    // features
    adaptedConfig = adaptFeatureServiceScraperPromptKey(
        configData,
        adaptedConfig
    );
    return adaptedConfig;
};

const isSectionVisible = (
    generalKey: AppConfigCoreSectionKey | AppConfigZitoneoSectionKey,
    ownerKey: OwnerConfigCoreSectionKey | OwnerConfigZitoneoSectionKey,
    configData: IApplicationConfiguration[],
    ownerConfigData: IHotelOwnerConfigurationValue[]
) => {
    const generalVisibilityInfo = configData.find(
        appConfig => appConfig.ApplicationConfigurationKey.Key === generalKey
    );
    const generalVisibilityDefaultValue =
        generalVisibilityInfo?.ApplicationConfigurationKey.DefaultValue ===
        'True';
    const generalVisibilityValue = generalVisibilityInfo?.Value
        ? generalVisibilityInfo.Value === 'True'
        : generalVisibilityDefaultValue;

    if (generalVisibilityValue) return true;

    return ownerConfigData.some(
        ownerConfig =>
            ownerConfig.Key === ownerKey &&
            ownerConfig.Enabled &&
            ownerConfig.AleConfigurationKey.Enabled &&
            ownerConfig.Value === 'True'
    );
};

// specific adapters for sections
const adaptZinantisSection = (
    zinantisMembership: boolean,
    adaptedConfig: IAppConfig
) => {
    return {
        ...adaptedConfig,
        sections: {
            ...adaptedConfig.sections,
            zinantis: {
                isVisible: zinantisMembership,
                pages: {
                    [AppSectionsZinantis.MEMBERS]: zinantisMembership,
                    [AppSectionsZinantis.CONNECTION_REQUESTS]:
                        zinantisMembership,
                    [AppSectionsZinantis.CHANNEL_MANAGERS]: zinantisMembership,
                },
            },
        },
    };
};

const adaptSupplementSection = (
    configData: IApplicationConfiguration[],
    ownerConfigData: IHotelOwnerConfigurationValue[],
    adaptedConfig: IAppConfig
) => {
    const isVisible = isSectionVisible(
        AppConfigCoreSectionKey.SUPPLEMENTS,
        OwnerConfigCoreSectionKey.SUPPLEMENTS,
        configData,
        ownerConfigData
    );

    return {
        ...adaptedConfig,
        sections: {
            ...adaptedConfig.sections,
            core: {
                ...adaptedConfig.sections.core,
                pages: {
                    ...adaptedConfig.sections.core.pages,
                    [AppSectionsCore.SUPPLEMENTS]: isVisible,
                },
            },
        },
    };
};

const adaptStopSalesSection = (
    configData: IApplicationConfiguration[],
    ownerConfigData: IHotelOwnerConfigurationValue[],
    adaptedConfig: IAppConfig
) => {
    const isVisible = isSectionVisible(
        AppConfigCoreSectionKey.STOP_SALES,
        OwnerConfigCoreSectionKey.STOP_SALES,
        configData,
        ownerConfigData
    );

    return {
        ...adaptedConfig,
        sections: {
            ...adaptedConfig.sections,
            core: {
                ...adaptedConfig.sections.core,
                pages: {
                    ...adaptedConfig.sections.core.pages,
                    [AppSectionsCore.STOP_SALES]: isVisible,
                },
            },
        },
    };
};

const adaptStaticContractsSection = (
    configData: IApplicationConfiguration[],
    ownerConfigData: IHotelOwnerConfigurationValue[],
    adaptedConfig: IAppConfig
) => {
    const isVisible = isSectionVisible(
        AppConfigCoreSectionKey.CONTRACTS,
        OwnerConfigCoreSectionKey.CONTRACTS,
        configData,
        ownerConfigData
    );

    return {
        ...adaptedConfig,
        sections: {
            ...adaptedConfig.sections,
            core: {
                ...adaptedConfig.sections.core,
                pages: {
                    ...adaptedConfig.sections.core.pages,
                    [AppSectionsCore.CONTRACTS]: isVisible,
                },
            },
        },
    };
};

const adaptZitoneoSection = (
    configData: IApplicationConfiguration[],
    ownerConfigData: IHotelOwnerConfigurationValue[],
    adaptedConfig: IAppConfig
) => {
    const isVisible = isSectionVisible(
        AppConfigZitoneoSectionKey.ZINOTEO_SERVICE_ATTRIBUTES,
        OwnerConfigZitoneoSectionKey.ZINOTEO_SERVICE_ATTRIBUTES,
        configData,
        ownerConfigData
    );

    return {
        ...adaptedConfig,
        sections: {
            ...adaptedConfig.sections,
            zitoneo: {
                ...adaptedConfig.sections.zitoneo,
                isVisible,
                pages: {
                    ...adaptedConfig.sections.zitoneo.pages,
                    [AppSectionsZitoneo.HOTEL_CONTENT]: isVisible,
                },
            },
        },
    };
};

// features

const adaptFeatureServiceScraperPromptKey = (
    configData: IApplicationConfiguration[],
    adaptedConfig: IAppConfig
) => {
    const serviceScraperPromptKeyConfig = configData.find(
        config =>
            config.ApplicationConfigurationKey.Key ===
            AppConfigFeaturesZitoneo.ServiceScraperPromptKey
    );

    const competitorScrapingPromptKeyConfig = configData.find(
        config =>
            config.ApplicationConfigurationKey.Key ===
            AppConfigFeaturesZitoneo.CompetitorScrapingPromptKey
    );

    return {
        ...adaptedConfig,
        features: {
            ...adaptedConfig.features,
            zitoneo: {
                ...adaptedConfig.features.zitoneo,
                serviceScraperPromptKey: serviceScraperPromptKeyConfig
                    ? serviceScraperPromptKeyConfig.Value
                    : '',
                competitorScrapingPromptKey: competitorScrapingPromptKeyConfig
                    ? competitorScrapingPromptKeyConfig.Value
                    : '',
            },
        },
    };
};
